import { useMutation, useQueryClient } from '@tanstack/react-query'

import { MANAGED_VENUES_QUERY_KEY } from '@/hooks/useManagedVenues'
import type { ApiError } from '@/lib/api/errors'
import { saveVenueProfile } from '@/lib/venue/venueProfileApi'
import type { VenueProfileSaveInput, VenueProfileView } from '@/lib/venue/venueProfileTypes'

import { venueProfileQueryKey } from './useVenueProfile'

/**
 * 구장 기본정보 저장 (#594 §2).
 *
 * 🚨 `useMutation` 을 쓴다 — 중복 결제 위험 같은 부작용이 여기엔 없고, PUT 은 멱등이라고 계약이 명시한다.
 *
 * 저장 성공 시:
 *  ① 응답(= GET 과 동일한 최신 View)을 프로필 쿼리 캐시에 **직접 반영**한다 — 재조회하지 않는다.
 *     Reader 복제 지연 때문에 재조회는 오히려 방금 저장한 값이 아니라 옛 값을 받아올 위험이 있다.
 *  ② 관리 구장 목록 쿼리(헤더·사이드바 구장명의 원천)를 무효화한다 — 구장명이 바뀌었을 수 있다.
 */
export function useSaveVenueProfile(venueId: string) {
  const queryClient = useQueryClient()

  return useMutation<VenueProfileView, ApiError, VenueProfileSaveInput>({
    mutationFn: (input) => saveVenueProfile(venueId, input),
    onSuccess: (data) => {
      queryClient.setQueryData(venueProfileQueryKey(venueId), data)
      void queryClient.invalidateQueries({ queryKey: MANAGED_VENUES_QUERY_KEY })
    },
  })
}
